import { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, ChevronLeft, ChevronRight, ReceiptText, X, Check, AlertTriangle, Clock, Printer } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { creditNotesService } from '@/services/credit-notes.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { CreditNoteTicket } from '@/components/CreditNoteTicket';
import { useReactToPrint } from 'react-to-print';
import { dialogs } from '@/stores/dialog-store';

const statusColors: Record<string, string> = {
  ACTIVE: 'bg-green-50 text-green-700 border-green-200',
  PARTIALLY_USED: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  FULLY_USED: 'bg-gray-50 text-gray-500 border-gray-200',
  EXPIRED: 'bg-red-50 text-red-500 border-red-200',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'Activa',
  PARTIALLY_USED: 'Parcialmente usada',
  FULLY_USED: 'Usada totalmente',
  EXPIRED: 'Expirada',
};

const statusIcons: Record<string, React.ReactNode> = {
  ACTIVE: <Check size={14} />,
  PARTIALLY_USED: <Clock size={14} />,
  FULLY_USED: <Check size={14} />,
  EXPIRED: <AlertTriangle size={14} />,
};

function formatNoteDate(value: unknown, withTime = true): string {
  if (value == null || value === '') return '—';
  const d = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', withTime
    ? { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }
    : { day: '2-digit', month: 'long', year: 'numeric' });
}

export function CreditNotesPage() {
  const { user } = useAuthStore();
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();

  const [creditNotes, setCreditNotes] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  // Sin filtro de fecha por defecto: así se ven todas las NC (incluida la de ayer)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedNote, setSelectedNote] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [noteToPrint, setNoteToPrint] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onAfterPrint: () => setNoteToPrint(null),
  });

  useEffect(() => {
    if (noteToPrint) {
      const t = setTimeout(() => handlePrint(), 100);
      return () => clearTimeout(t);
    }
  }, [noteToPrint, handlePrint]);

  const limit = 20;

  const loadCreditNotes = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const result = await creditNotesService.findAll({
        companyId: user.company.id,
        status: statusFilter || undefined,
        startDate: startDate ? new Date(`${startDate}T00:00:00`).toISOString() : undefined,
        endDate: endDate ? new Date(`${endDate}T23:59:59`).toISOString() : undefined,
        page,
        limit,
      });
      setCreditNotes(result.data || []);
      setTotal(result.total || 0);
      setTotalPages(result.totalPages || 1);
    } catch (err: any) {
      handleApiError(err);
    }
    setLoading(false);
  }, [user, statusFilter, startDate, endDate, page]);

  useEffect(() => {
    loadCreditNotes();
  }, [loadCreditNotes]);

  const viewDetail = async (id: string) => {
    try {
      const note = await creditNotesService.findById(id);
      setSelectedNote(note);
      setShowDetail(true);
    } catch (err: any) {
      // No tumbar toda la pantalla: solo avisar
      await dialogs.alert(
        err?.response?.data?.message || err?.response?.data?.error || 'No se pudo abrir la nota de crédito',
        'Error',
      );
    }
  };

  const reprint = (note: any) => {
    setNoteToPrint({
      ...note,
      folio: note.folio,
      createdAt: note.createdAt || new Date().toISOString(),
      items: note.items,
      originalSale: note.originalSale,
    });
  };

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadCreditNotes} onLogin={handleAuthRedirect} />
      {!error && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold">Notas de Crédito</h1>
              <p className="text-sm text-gray-500 mt-1">{total} notas registradas</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm"
            />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="ios-input h-9 text-sm"
            >
              <option value="">Todos los estados</option>
              <option value="ACTIVE">Activas</option>
              <option value="PARTIALLY_USED">Parcialmente usadas</option>
              <option value="FULLY_USED">Usadas totalmente</option>
              <option value="EXPIRED">Expiradas</option>
            </select>
          </div>

          {/* Credit Notes table */}
          <div className="ios-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">ID</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Fecha</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Venta original</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Creado por</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Total</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Saldo</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider">Estado</th>
                    <th className="text-center px-4 py-3 font-medium text-gray-500 text-xs uppercase tracking-wider"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12">
                        <Loader2 size={24} className="animate-spin mx-auto text-gray-400" />
                      </td>
                    </tr>
                  ) : creditNotes.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-gray-500 text-sm">
                        No se encontraron notas de crédito
                      </td>
                    </tr>
                  ) : (
                    creditNotes.map((note) => {
                      const remaining = Number(note.remaining);
                      const totalAmount = Number(note.total);
                      const usagePercent = totalAmount > 0 ? ((totalAmount - remaining) / totalAmount) * 100 : 0;
                      return (
                        <tr
                          key={note.id}
                          className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                          onClick={() => viewDetail(note.id)}
                        >
                          <td className="px-4 py-3">
                            <span className="font-mono font-bold text-teal-700 tracking-wider">
                              {note.folio}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {formatNoteDate(note.createdAt)}
                          </td>
                          <td className="px-4 py-3 font-medium">
                            {note.originalSale?.folio || '—'}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {note.createdBy?.fullName || '—'}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold">
                            ${totalAmount.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold ${remaining > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                              ${remaining.toFixed(2)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${statusColors[note.status] || ''}`}>
                              {statusIcons[note.status]}
                              {statusLabels[note.status] || note.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden mx-auto">
                              <div
                                className="h-full rounded-full transition-all bg-teal-500"
                                style={{ width: `${Math.min(100, usagePercent)}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <span className="text-xs text-gray-500">Página {page} de {totalPages}</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Detail Modal */}
          {showDetail && selectedNote && (
            <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-[20px] w-full max-w-lg max-h-[90vh] flex flex-col shadow-ios-xl">
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                      <ReceiptText size={16} className="text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Nota de Crédito</h3>
                      <p className="text-xs text-gray-500 font-bold tracking-wider">{selectedNote.folio}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowDetail(false)} className="p-2 rounded-lg hover:bg-gray-100">
                    <X size={18} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  {/* Status badge */}
                  <div className="flex justify-center">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusColors[selectedNote.status] || ''}`}>
                      {statusIcons[selectedNote.status]}
                      {statusLabels[selectedNote.status] || selectedNote.status}
                    </span>
                  </div>

                  {/* Amounts */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-teal-50 rounded-[10px] p-4 text-center border border-teal-100">
                      <p className="text-[10px] text-teal-600 uppercase font-bold tracking-wider">Total</p>
                      <p className="text-2xl font-black text-teal-700 mt-1">
                        ${Number(selectedNote.total).toFixed(2)}
                      </p>
                    </div>
                    <div className={`rounded-[10px] p-4 text-center border ${
                      Number(selectedNote.remaining) > 0
                        ? 'bg-green-50 border-green-100'
                        : 'bg-gray-50 border-gray-100'
                    }`}>
                      <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Saldo disponible</p>
                      <p className={`text-2xl font-black mt-1 ${
                        Number(selectedNote.remaining) > 0 ? 'text-green-700' : 'text-gray-400'
                      }`}>
                        ${Number(selectedNote.remaining).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Usage progress */}
                  {Number(selectedNote.total) > 0 && (
                    <div>
                      <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                        <span>Uso</span>
                        <span>
                          {Number(selectedNote.total) > 0
                            ? `${((1 - Number(selectedNote.remaining) / Number(selectedNote.total)) * 100).toFixed(0)}%`
                            : '0%'}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full bg-teal-500 transition-all"
                          style={{
                            width: `${Number(selectedNote.total) > 0
                              ? Math.min(100, (1 - Number(selectedNote.remaining) / Number(selectedNote.total)) * 100)
                              : 0}%`
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Info */}
                  <div className="bg-gray-50 rounded-[10px] p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Venta original</span>
                      <span className="text-sm font-semibold">
                        {selectedNote.originalSale?.folio || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Cliente</span>
                      <span className="text-sm font-medium">
                        {selectedNote.originalSale?.customer?.name || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Creado por</span>
                      <span className="text-sm font-medium">
                        {selectedNote.createdBy?.fullName || '—'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Fecha de creación</span>
                      <span className="text-sm font-medium">
                        {formatNoteDate(selectedNote.createdAt)}
                      </span>
                    </div>
                    {selectedNote.updatedAt && formatNoteDate(selectedNote.updatedAt) !== '—' && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Última actualización</span>
                        <span className="text-sm font-medium">
                          {formatNoteDate(selectedNote.updatedAt)}
                        </span>
                      </div>
                    )}
                    {selectedNote.expiresAt && formatNoteDate(selectedNote.expiresAt) !== '—' && (
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Vence</span>
                        <span className="text-sm font-medium">
                          {formatNoteDate(selectedNote.expiresAt, false)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Reason */}
                  {selectedNote.reason && (
                    <div className="bg-orange-50 border border-orange-200 rounded-[10px] p-3">
                      <p className="text-[10px] text-orange-600 uppercase font-bold tracking-wider mb-1">Motivo</p>
                      <p className="text-sm text-orange-800">{selectedNote.reason}</p>
                    </div>
                  )}

                  {/* Items returned */}
                  {selectedNote.items && (() => {
                    try {
                      const items = typeof selectedNote.items === 'string'
                        ? JSON.parse(selectedNote.items)
                        : selectedNote.items;
                      if (!items || items.length === 0) return null;
                      return (
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-2">Productos devueltos</h4>
                          <div className="space-y-1.5">
                            {items.map((item: any, idx: number) => (
                              <div key={idx} className="flex items-center justify-between text-sm bg-gray-50 rounded-[8px] p-2.5">
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs font-medium truncate">{item.name || 'Producto'}</p>
                                  <p className="text-[10px] text-gray-500">
                                    {item.quantity} x ${Number(item.unitPrice).toFixed(2)}
                                    {item.discount > 0 && ` - Dto: $${(item.discount * item.quantity).toFixed(2)}`}
                                  </p>
                                </div>
                                <p className="text-xs font-semibold">
                                  ${(item.netTotal || item.unitPrice * item.quantity).toFixed(2)}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    } catch {
                      return null;
                    }
                  })()}
                </div>

                <div className="p-4 border-t border-gray-200 flex gap-2">
                  <button
                    onClick={() => reprint(selectedNote)}
                    className="flex-1 py-2.5 rounded-[10px] border border-teal-200 text-teal-700 hover:bg-teal-50 text-sm font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Printer size={16} />
                    Reimprimir
                  </button>
                  <button
                    onClick={() => setShowDetail(false)}
                    className="flex-1 py-2.5 rounded-[10px] bg-gray-100 hover:bg-gray-200 text-sm font-medium transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}

          <div aria-hidden style={{ position: 'fixed', left: '-10000px', top: 0, width: '80mm' }}>
            {noteToPrint && (
              <CreditNoteTicket
                creditNote={noteToPrint}
                originalSale={noteToPrint.originalSale}
                ref={printRef}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
